import React, { useState } from 'react';
import { cloneDeep } from 'lodash';
import { Record, RecordTerms } from './record.types';
import useFormFields, { Instruction } from '../hooks/use-form-fields';
import { SystemAppData } from '../hooks/use-system-app';
import { linkParentRecord } from '../utils/nested-modal-helper';
import EmptyTaskObject from '../tasks/__fixtures__/empty-task-object';

interface Props {
  title?: string;
  creating?: boolean;
  row: Record<object> | null;
  formData?: SystemAppData | null;
  handleViewRecord?: (record: Record<any> | null) => void;
  handleCloseModal: (record: Record<any> | null) => void;
  openRecordInEditor: (
    parentRecord?: Record<any>,
    record?: Record<any>,
    formData?: SystemAppData,
    shouldLinkToParent?: boolean
  ) => void;
  terminology?: RecordTerms;
  specialInstructions?: Instruction[];
  useLegacyEditor?: boolean;
}

interface EditorState {
  record: Record<any> | null,
  formData?: SystemAppData,
  shouldLinkToParent: boolean,
  parent?: Record<any>,
}

export default function editorStack(Editor: React.FC<Props>): typeof Editor {
  return (props: Props) => {
    const {
      specialInstructions, terminology, creating, title: propTitle, row, useLegacyEditor = false,
      handleViewRecord,
    } = props;

    const [record, setRecord] = useState<Record<any> | null>(row);
    const [isCreating, setCreating] = useState(creating);
    const [title, setTitle] = useState<string>(propTitle || 'record');
    // @ts-ignore
    const [formData, setFormData] = useState<SystemAppData>(props.formData);
    const { elements: fields } = useFormFields(cloneDeep(formData?.restForm), specialInstructions);

    const initialState = { record, formData, shouldLinkToParent: false };
    const [stack, setEditorStack] = useState<Array<EditorState>>([initialState]);

    const moveToEditorState = (
      newStack: EditorState[],
      state: EditorState,
      nowCreating: boolean,
    ) => {
      setTitle(state.formData?.restForm.system_type === 'basic_task' ? 'task' : 'issue');
      setEditorStack(newStack);
      setCreating(nowCreating);
      setRecord(state.record);
      // @ts-ignore
      setFormData(state.formData);
    };

    const handleCloseEditor = (lastRecord: Record<any> | null) => {
      if (stack.length > 1) {
        const currentEditorState = stack.pop();
        // Get the prior (parent) form data, if there is one
        const previousEditorState: EditorState = stack.length > 0
          ? stack[stack.length - 1]
          : { record: null, formData, shouldLinkToParent: false };
        // Update the record if it has a new link
        if (currentEditorState?.shouldLinkToParent) {
          // @ts-ignore
          previousEditorState.record = linkParentRecord(currentEditorState.parent, lastRecord);
        }
        moveToEditorState(stack, previousEditorState, !previousEditorState?.record?.id);
      } else {
        setCreating(false);
        setRecord(null);
        props.handleCloseModal(lastRecord);
      }
    };

    const handleEditRecord = (
      parentRecord?: Record<any>,
      nextRecord?: Record<any>,
      linkedRecordFormData?: SystemAppData,
      shouldLinkToParent = false,
    ) => {
      const newOrExistingRecord = nextRecord ?? EmptyTaskObject(linkedRecordFormData?.restForm?.id || '', 1);
      const nextEditorState: EditorState = {
        record: newOrExistingRecord,
        formData: linkedRecordFormData,
        parent: parentRecord,
        shouldLinkToParent,
      };
      moveToEditorState([...stack, nextEditorState], nextEditorState, !nextRecord);

      props.openRecordInEditor(parentRecord, nextRecord, linkedRecordFormData, shouldLinkToParent);
    };

    const handleUpdateInitialState = (currentRecord: Record<any> | null) => {
      const baseState: EditorState = stack[0];
      const newStack = stack;
      newStack[0] = {
        ...baseState,
        record: currentRecord,
      };
      setEditorStack(newStack);
    };

    return (
      <Editor
        key={stack.length}
        useLegacyEditor={useLegacyEditor}
        title={title}
        row={record}
        formData={formData}
        creating={isCreating}
        handleViewRecord={handleViewRecord}
        handleCloseModal={handleCloseEditor}
        openRecordInEditor={handleEditRecord}
        // @ts-ignore
        handleUpdateInitialState={handleUpdateInitialState}
        fields={fields}
        terminology={terminology}
        specialInstructions={specialInstructions}
      />
    );
  };
}
